import { FC, useEffect, useMemo, useState } from 'react';
import './ProductCategory.css';
import GeneralInput from '../../../main/components/generalInput/GeneralInput';
import { useForm } from 'react-hook-form';
import IProductCategory from '../../../main/interfaces/IProductCategory';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import GeneralSelect from '../../../main/components/select/GeneralSelect';
import axiosInstance from '../../../main/initializers/axios';
import { useTranslation } from 'react-i18next';

interface Props {
  isProductCategory: boolean;
  handleClick?: () => void;
  header: string;
}

type CategoryTypeItem = { Id: number; code: 'Bar' | 'Restaurant'; label: string };

const ProductCategory: FC<Props> = (props: Props) => {
  const { isProductCategory, header } = props;
  const [categories, setCategories] = useState<IProductCategory[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Partial<IProductCategory>>({
    code: '',
    name: '',
    type: 1,
  });
  const { i18n } = useTranslation();

  // 🔹 Enum ProductType: 1 = Bar, 2 = Restaurant
  const categoryType = useMemo(
    () => [
      { id: 1, name: i18n.t('productType.Bar') },
      { id: 2, name: i18n.t('productType.Restaurant') },
    ],
    [i18n.language], // rifresko tekstet kur ndryshon gjuha
  );

  const getCanDelete = (item?: IProductCategory | null) => {
    if (!item) return true;
    const raw = item as IProductCategory & { canDelete?: boolean; CanDelete?: boolean };
    return raw.canDelete ?? raw.CanDelete ?? true;
  };

  const getCategory = () => {
    axiosInstance.get(ApiConstants.Products.productCategory).then((res: any) => {
      setCategories(res.data ?? []);
    });
  };

  useEffect(() => {
    getCategory();
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IProductCategory>({
    defaultValues: {
      name: '',
      code: '',
      type: categoryType[0]?.id ?? 1, // 1 = Bar
    },
  });

  const onSubmit = (data: IProductCategory) => {
    const params = {
      name: data.name,
      code: data.code,
      type: Number(data.type) || 1, // backend pret 1/2 sipas enum ProductType
    };

    axiosInstance.post(ApiConstants.Products.productCategory, params).then((response: any) => {
      if (response.status === 200) {
        toast.success(i18n.t('productCategory.createdSuccessfully'));
        getCategory();
      } else {
        toast.error(i18n.t('productCategory.somethingWentWrong'));
      }
    });
  };

  const startEdit = (item: IProductCategory) => {
    setEditingId(Number(item.id));
    setEditValues({
      id: item.id,
      name: item.name ?? '',

      code: item.code ?? '',
      type: Number(item.type) || 1,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({ code: '', name: '', type: 1 });
  };

  const saveEdit = () => {
    const id = Number(editValues.id ?? 0);
    if (!id) return;

    const params = {
      id,
      name: String(editValues.name ?? '').trim(),
      code: String(editValues.code ?? '').trim(),
      type: Number(editValues.type) || 1,
    };

    axiosInstance.post(ApiConstants.Products.productCategory, params).then((response: any) => {
      if (response.status === 200) {
        toast.success(i18n.t('umb.updatedSuccessfully'));
        cancelEdit();
        getCategory();
      } else {
        toast.error(i18n.t('productCategory.somethingWentWrong'));
      }
    });
  };

  const deleteCategory = (id: number) => {
    const category = categories.find((item) => Number(item.id) === Number(id));

    if (!getCanDelete(category)) {
      toast.error(i18n.t('generic.categoryUsedInProductsCannotDelete'));
      return;
    }

    axiosInstance
      .delete(`${ApiConstants.Products.productCategory}/delete?id=${id}`)
      .then((response) => {
        if (response.status === 200) {
          toast.success(i18n.t('productCategory.deletedSuccessfully'));
          if (editingId === id) cancelEdit();
          getCategory();
          return;
        }

        toast.error(i18n.t('productCategory.somethingWentWrong'));
      })
      .catch((error) => {
        if (error?.response?.status === 409) {
          toast.error(i18n.t('generic.categoryUsedInProductsCannotDelete'));
          return;
        }

        toast.error(i18n.t('productCategory.somethingWentWrong'));
      });
  };

  return (
    <div className="pcWrap">
      {/* Header */}
      <div className="pcHeader">
        <h3>{header}</h3>
      </div>

      {isProductCategory ? (
        <div className="pcBody">productcategory</div>
      ) : (
        <div className="pcBody">
          {/* Create */}
          <form onSubmit={handleSubmit(onSubmit)} className="pcCreate">
            <GeneralInput
              required
              label={i18n.t('productCategory.nameLabel')}
              placeholder={i18n.t('productCategory.namePlaceholder')}
              name="name"
              type="text"
              errors={errors}
              control={control}
            />

            <GeneralInput
              label={i18n.t('productCategory.codeLabel')}
              placeholder={i18n.t('productCategory.codePlaceholder')}
              name="code"
              type="text"
              errors={errors}
              control={control}
            />

            <GeneralSelect
              preSelectedVal={i18n.t('productCategory.typePlaceholder')}
              setSelectedVal={() => {}}
              data={categoryType}
              required={true}
              control={control}
              label={i18n.t('productCategory.typeLabel')}
              errors={errors}
              name="type"
              value="name" // ruajmë Id = 1, 2 në formData
            />

            <button type="submit" className="ui-control ui-btn ui-btn--primary pcAddBtn">
              +
            </button>
          </form>

          {/* List */}
          <div className="pcList">
            {categories.map((item, index) => (
              <div key={item.id} className="pcRow bcCard">
                {editingId === item.id ? (
                  <>
                    <div className="pcField">
                      <label className="giLabel">{i18n.t('productCategory.nameLabel')}</label>
                      <input
                        className="giInput"
                        type="text"
                        value={String(editValues.name ?? '')}
                        onChange={(e) => setEditValues((prev) => ({ ...prev, name: e.target.value }))}
                      />
                    </div>

                    <div className="pcField">
                      <label className="giLabel">{i18n.t('productCategory.codeLabel')}</label>
                      <input
                        className="giInput"
                        type="text"
                        value={String(editValues.code ?? '')}
                        onChange={(e) => setEditValues((prev) => ({ ...prev, code: e.target.value }))}
                      />
                    </div>

                    <div className="pcField">
                      <label className="giLabel">{i18n.t('productCategory.typeLabel')}</label>
                      <select
                        className="giInput pcNativeSelect"
                        value={Number(editValues.type ?? 1)}
                        onChange={(e) => setEditValues((prev) => ({ ...prev, type: Number(e.target.value) }))}
                      >
                        {categoryType.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <GeneralInput
                      disabled={true}
                      label={i18n.t('productCategory.nameLabel')}
                      placeholder={i18n.t('productCategory.namePlaceholder')}
                      name={`name${index}`}
                      type="text"
                      control={control}
                      errors={errors}
                      dfvalue={item.name}
                    />

                    <GeneralInput
                      disabled={true}
                      label={i18n.t('productCategory.codeLabel')}
                      placeholder={i18n.t('productCategory.codePlaceholder')}
                      name={`code${index}`}
                      type="text"
                      control={control}
                      errors={errors}
                      dfvalue={item.code}
                    />

                    <div className="pcField">
                      <label className="giLabel">{i18n.t('productCategory.typeLabel')}</label>
                      <input
                        className="giInput"
                        type="text"
                        disabled
                        value={categoryType.find((type) => type.id === Number(item.type))?.name ?? ''}
                        readOnly
                      />
                    </div>
                  </>
                )}

                <div className="pcActions">
                  {editingId === item.id ? (
                    <>
                      <button type="button" className="pcIconBtn pcSaveBtn" onClick={saveEdit} title={i18n.t('actions.save')}>
                        <SaveIcon />
                      </button>
                      <button type="button" className="pcIconBtn" onClick={cancelEdit} title={i18n.t('actions.cancel')}>
                        <CloseIcon />
                      </button>
                    </>
                  ) : (
                    <button type="button" className="pcIconBtn" onClick={() => startEdit(item)} title={i18n.t('actions.edit')}>
                      <EditIcon />
                    </button>
                  )}

                  <button
                    type="button"
                    className="pcDeleteBtn"
                    onClick={() => deleteCategory(item.id)}
                    title={!getCanDelete(item) ? i18n.t('generic.categoryUsedInProductsCannotDelete') : i18n.t('actions.delete')}
                    disabled={!getCanDelete(item)}
                  >
                    <DeleteForeverIcon className="pcDeleteIcon" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCategory;
